/**
 * JSON Deserialization utilities for handling Newtonsoft.Json serialization from backend
 * Handles:
 * - TypeNameHandling.Auto (polymorphic types with $type property)
 * - Custom GenericCollectionJsonConverter (items array with $type/$value wrappers)
 * - Map<string, T> conversion
 * 
 * 
 * Only applies GenericCollectionJsonConverter logic to these specific classes:
 * - SystemChildren
 * - ProductPromotionCollection
 */

export class JsonDeserializer {
  
  /**
   * Classes that use GenericCollectionJsonConverter on the backend
   * These are serialized as: { property1: value, items: [{$type, $value}, ...] }
   * Property names come in camelCase from the backend resolver.
   */
  private static readonly COLLECTION_CONVERTER_CLASSES = [
    'SystemChildren', 
    'ProductPromotionCollection'
  ];

  /**
   * Deserializes JSON that may contain Newtonsoft.Json specific structures
   * including collections serialized with GenericCollectionJsonConverter
   */
  static deserialize<T>(json: any, parentKey?: string): T {
    if (json === null || json === undefined) {
      return json;
    }

    // Handle arrays
    if (Array.isArray(json)) {
      return json.map(item => this.deserialize(item)) as any;
    }

    // Handle objects
    if (typeof json === 'object') {

      // Strip $type metadata from TypeNameHandling.Auto (if present at root level)
      json = this.stripTypeMetadata(json);

      // Check if this object is a GenericCollectionJsonConverter structure
      // It has both an "Items" array and other properties
      if (this.isCollectionConverterStructure(json, parentKey)) {
        return this.deserializeCollectionConverter(json) as T;
      }

      const result: any = {};

      for (const key in json) {
        if (!json.hasOwnProperty(key)) continue;

        const value = json[key];

        // Handle Map serialization (C# Dictionary)
        if (this.isSerializedMap(value)) {
          result[key] = this.deserializeMap(value);
        }
        // Handle nested objects/arrays, passing the key for context
        else {
          result[key] = this.deserialize(value, key);
        }
      }

      return result as T;
    }

    // Primitive types
    return json;
  }

  /**
   * Checks if an object is a collection serialized by GenericCollectionJsonConverter
   * These have an "Items" array and are for specific known classes
   */
  private static isCollectionConverterStructure(obj: any, parentKey?: string): boolean {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return false;
    }

    // Resolve the items key — backend resolver sends camelCase "items",
    // but also support PascalCase "Items" for backward compatibility
    const itemsKey = this.getItemsKey(obj);
    if (!itemsKey || !Array.isArray(obj[itemsKey])) {
      return false;
    }

    // Check if the parent key matches one of our known collection classes (camelCase)
    if (parentKey) {
      const camelCaseClasses = this.COLLECTION_CONVERTER_CLASSES.map(c => 
        c.charAt(0).toLowerCase() + c.slice(1)
      );
      if (camelCaseClasses.includes(parentKey)) {
        return true;
      }
    }

    // Also check if there are additional properties besides items/Items
    // GenericCollectionJsonConverter always creates an object with items + other props
    const keys = Object.keys(obj);
    const hasOtherProps = keys.some(k => k !== itemsKey);
    
    return hasOtherProps;
  }

  /**
   * Gets the items key from a collection converter object.
   * Supports both camelCase "items" (from backend resolver) and PascalCase "Items" (legacy).
   */
  private static getItemsKey(obj: any): string | null {
    if ('items' in obj) return 'items';
    if ('Items' in obj) return 'Items';
    return null;
  }

  /**
   * Deserializes a collection that uses GenericCollectionJsonConverter
   * Converts from: { prop1: value, items: [{$type, $value}, ...] }
   * To: An array with properties attached: [...items, prop1: value]
   * Supports both camelCase "items" (from backend resolver) and PascalCase "Items" (legacy)
   */
  private static deserializeCollectionConverter(obj: any): any {
    const itemsKey = this.getItemsKey(obj)!;
    const itemsArray = obj[itemsKey];
    
    // Unwrap the items array, handling $type/$value wrappers
    const items = (itemsArray || []).map((item: any) => this.unwrapTypedValue(item));
    
    // Attach additional properties (everything except the items key) to the array
    Object.keys(obj).forEach(key => {
      if (key !== itemsKey) {
        (items as any)[key] = this.deserialize(obj[key]);
      }
    });
    
    return items;
  }

  /**
   * Unwraps values that have $type and $value properties
   * This format is used by GenericCollectionJsonConverter for polymorphic collection items
   */
  private static unwrapTypedValue(item: any): any {
    if (item && typeof item === 'object' && '$type' in item && '$value' in item) {
      // Extract the actual value and recursively deserialize it
      return this.deserialize(item.$value);
    }
    return this.deserialize(item);
  }

  /**
   * Strips the $type property added by TypeNameHandling.Auto
   * This property is used for polymorphism on the backend but isn't needed in TypeScript
   * The type information is preserved through TypeScript's type system instead
   */
  private static stripTypeMetadata(obj: any): any {
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      const { $type, ...rest } = obj;
      
      if ($type) {
        // Optionally store type info for debugging/logging
        // console.log('Stripped type metadata:', $type);
      }
      
      return rest;
    }
    return obj;
  }

  /**
   * Checks if an object is a serialized Map/Dictionary
   */
  private static isSerializedMap(value: any): boolean {
    // C# Dictionary is serialized as array of key-value pairs by default
    // or as object with string keys
    return false; // Customize based on your backend serialization
  }

  /**
   * Deserializes a Map/Dictionary from JSON
   */
  private static deserializeMap(value: any): Map<string, any> {
    const map = new Map<string, any>();
    if (Array.isArray(value)) {
      // Array format: [{Key: 'x', Value: 'y'}, ...]
      value.forEach(pair => {
        if (pair.Key !== undefined && pair.Value !== undefined) {
          map.set(pair.Key, this.deserialize(pair.Value));
        }
      });
    } else if (typeof value === 'object') {
      // Object format: {"key1": "value1", "key2": "value2"}
      for (const key in value) {
        if (value.hasOwnProperty(key)) {
          map.set(key, this.deserialize(value[key]));
        }
      }
    }
    
    return map;
  }
  /**
   * Post-processes deserialized data to convert specific structures
   * Note: This is now handled automatically in the deserialize() method
   * This method is kept for backwards compatibility but is no longer needed
   * 
   * @deprecated Use deserialize() directly, which now handles collection extensions automatically
   */
  static processCollectionExtensions<T>(data: any, collectionProperties: string[] = []): T {
    console.warn('processCollectionExtensions is deprecated. Use deserialize() directly.');
    return data as T;
  }
}
